import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { UpdateOwnContact, type Party } from '@syndiqia/registry/contracts';
import { errorMessage, useApi } from '../api';
import { useMyUnits } from '../comms-queries';
import { Alert, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useUnits } from '../queries';
import { useMe, useSyndicate } from '../session';
import { ContactFields } from './MemberPage';
import { CertificateCard, useInsurance, useUploadCertificate } from './InsurancePage';
import type { Certificate, InsuranceSettings } from '@syndiqia/insurance/contracts';

/** "My profile": members update their own contact details (Law 25: right of rectification) and see their units. */
export function ProfilePage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const m = t.members;
  const contact = useQuery({ queryKey: [s.tenantId, 'me', 'contact'], queryFn: async () => (await api<{ contact: Party | null }>(`${s.api}/registry/me/contact`)).contact });
  const myUnits = useMyUnits();
  const units = useUnits();
  return (
    <>
      <PageHeader title={m.profileTitle} intro={m.profileIntro} />
      {contact.isPending && <Loading label={t.loading} />}
      {contact.isError && <Alert>{errorMessage(contact.error, t)}</Alert>}
      {contact.data === null && <Alert tone="info">{m.noRecord}</Alert>}
      {contact.data && <ContactForm party={contact.data} />}
      {s.can('insurance.submit') && contact.data && <MyInsurance />}
      {s.can('requests.manage') && <DigestPreference />}
      <LoginEmail />
      {!!myUnits.data?.length && (
        <section className="panel">
          <h2>{m.myUnits}</h2>
          <ul className="list">
            {myUnits.data.map((id) => (
              <li key={id}>
                <Link to={`${s.base}/units/${id}`}>{units.data?.find((u) => u.id === id)?.label ?? '…'}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function ContactForm({ party }: { party: Party }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const m = t.members;
  const [values, setValues] = useState({ email: party.email ?? '', phone: party.phone ?? '', preferredLanguage: party.preferredLanguage as string, mailingAddress: party.mailingAddress ?? '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    mutationFn: (input: UpdateOwnContact) => api<Party>(`${s.api}/registry/me/contact`, { method: 'PUT', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'me'] }),
  });
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => (setValues((v) => ({ ...v, [key]: e.target.value })), save.reset());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(UpdateOwnContact, values);
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data);
  };
  return (
    <form className="panel form" onSubmit={submit} noValidate>
      <h2>{party.displayName}</h2>
      <ContactFields values={values} set={set} errors={errors} />
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      {save.isSuccess && <Alert tone="info">{m.saved}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
      </div>
    </form>
  );
}

/** The member's insurance certificate: upload (the agent reads it), confirm the details, see the board's decision. */
function MyInsurance() {
  const { t, money } = useI18n();
  const ins = t.insurance;
  const mine = useInsurance<Certificate[]>(['mine'], '/mine');
  const settings = useInsurance<InsuranceSettings>(['settings'], '/settings');
  const upload = useUploadCertificate();
  return (
    <section className="panel">
      <h2>{ins.mineTitle}</h2>
      {settings.data && <p className="muted small">{ins.mineIntro(money(settings.data.requiredLiabilityCents))}</p>}
      {mine.data?.length === 0 && <p className="muted">{ins.noneYet}</p>}
      {mine.data?.slice(0, 3).map((c) => (
        <CertificateCard key={c.id} certificate={c} />
      ))}
      <label className="button button-primary upload-label">
        {upload.isPending ? ins.uploading : ins.upload}
        <input type="file" hidden accept="application/pdf,image/*" disabled={upload.isPending} onChange={(e) => e.target.files?.[0] && upload.mutate({ file: e.target.files[0] })} />
      </label>
      {upload.isError && <Alert>{(upload.error as Error).message}</Alert>}
    </section>
  );
}

/** The account's sign-in email (global, all syndicates): Keycloak sends a verification link to the new address. */
function LoginEmail() {
  const { t } = useI18n();
  const api = useApi();
  const me = useMe();
  const m = t.members;
  const [email, setEmail] = useState('');
  const change = useMutation({ mutationFn: (value: string) => api<{ pending: string }>('/api/me/email', { method: 'POST', body: { email: value } }) });
  const current = me.data?.user.email;
  if (!current) return null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (email.trim()) change.mutate(email.trim(), { onSuccess: () => setEmail('') });
  };
  return (
    <form className="panel form" onSubmit={submit}>
      <h2>{m.loginEmail}</h2>
      <p className="muted small">{m.loginEmailIntro(current)}</p>
      <div className="row">
        <Field label={m.newLoginEmail} hint={m.loginEmailHint}>
          <input type="email" value={email} onChange={(e) => (setEmail(e.target.value), change.reset())} autoComplete="email" required />
        </Field>
      </div>
      {change.isError && <Alert>{errorMessage(change.error, t)}</Alert>}
      {change.data && <Alert tone="info">{m.loginEmailPending(change.data.pending)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button" disabled={change.isPending || !email.trim()}>
          {m.changeLoginEmail}
        </button>
      </div>
    </form>
  );
}

/** Board members and managers: how often they receive the board's digest email for this client. */
function DigestPreference() {
  const { t } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const m = t.members;
  const pref = useQuery({ queryKey: [s.tenantId, 'notification-prefs'], queryFn: () => api<{ digest: 'daily' | 'weekly' | 'off' }>(`${s.api}/notification-prefs`) });
  const save = useMutation({
    mutationFn: (digest: 'daily' | 'weekly' | 'off') => api(`${s.api}/notification-prefs`, { method: 'PUT', body: { digest } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'notification-prefs'] }),
  });
  if (!pref.data) return null;
  return (
    <section className="panel form">
      <h2>{m.digestTitle}</h2>
      <p className="muted small">{m.digestIntro}</p>
      <div className="segmented" role="group" aria-label={m.digestTitle}>
        {(['daily', 'weekly', 'off'] as const).map((d) => (
          <button key={d} type="button" className={pref.data.digest === d ? 'active' : undefined} disabled={save.isPending} onClick={() => save.mutate(d)}>
            {m.digestOptions[d]}
          </button>
        ))}
      </div>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
    </section>
  );
}
